/**
 * MyOrders (Sprint 5 B6) — page "Đơn hàng của tôi".
 *
 * Layout:
 *   - Breadcrumb
 *   - Tabs filter: Tất cả / Chờ thanh toán / Đã thanh toán / Đã huỷ
 *   - Table với amount + status tag + payment_link button cho PENDING
 */

import { useState } from "react";
import {
  Breadcrumb,
  Button,
  Empty,
  Result,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useMyOrders, type Order, type OrderStatus } from "@/api/payment";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Paragraph } = Typography;

type TabKey = "all" | OrderStatus;

const STATUS_META: Record<OrderStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: "Chờ thanh toán", color: "blue", icon: <ClockCircleOutlined /> },
  paid: { label: "Đã thanh toán", color: "success", icon: <CheckCircleOutlined /> },
  cancelled: { label: "Đã huỷ", color: "default", icon: <CloseCircleOutlined /> },
  refunded: { label: "Đã hoàn tiền", color: "gold", icon: <CloseCircleOutlined /> },
  failed: { label: "Thất bại", color: "error", icon: <CloseCircleOutlined /> },
};

export function MyOrders() {
  const [tab, setTab] = useState<TabKey>("all");
  const statusFilter = tab === "all" ? undefined : tab;
  const { data: orders, isLoading, error } = useMyOrders(statusFilter);

  const columns = [
    {
      title: "Đơn hàng",
      dataIndex: "id",
      key: "id",
      render: (id: number) => <strong>#{id}</strong>,
    },
    {
      title: "Môn học",
      dataIndex: "course",
      key: "course",
      render: (course: Order["course"], record: Order) =>
        course ? (
          <Link to={`/my-courses/${course.slug}`}>
            <strong>{course.code}</strong> · {course.name}
          </Link>
        ) : (
          <span>{record.items.length} môn học</span>
        ),
    },
    {
      title: "Số tiền",
      dataIndex: "amount_display",
      key: "amount",
      align: "right" as const,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (status: OrderStatus) => {
        const meta = STATUS_META[status];
        return (
          <Tag icon={meta.icon} color={meta.color}>
            {meta.label}
          </Tag>
        );
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      render: (iso: string) => formatDateTime(iso),
    },
    {
      title: "Thao tác",
      key: "actions",
      render: (_: unknown, record: Order) => {
        if (record.status === "pending" && record.payment_link) {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                window.location.href = record.payment_link;
              }}
            >
              Tiếp tục thanh toán
            </Button>
          );
        }
        if (record.status === "paid") {
          return (
            <Link to={record.course ? `/my-courses/${record.course.slug}` : "/my-courses"}>
              <Button size="small">Vào khóa</Button>
            </Link>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: "Đơn hàng" },
        ]}
      />

      <Title level={2}>
        <ShoppingOutlined /> Đơn hàng của tôi
      </Title>
      <Paragraph type="secondary">
        Quản lý lịch sử đơn hàng và thanh toán môn học.
      </Paragraph>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as TabKey)}
        items={[
          { key: "all", label: "Tất cả" },
          { key: "pending", label: STATUS_META.pending.label },
          { key: "paid", label: STATUS_META.paid.label },
          { key: "cancelled", label: STATUS_META.cancelled.label },
        ]}
      />

      {isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {error && (
        <Result
          status="error"
          title="Không tải được danh sách"
          subTitle={String(error)}
        />
      )}

      {!isLoading && orders && orders.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            tab === "all"
              ? "Bạn chưa có đơn hàng nào."
              : `Không có đơn hàng ${STATUS_META[tab as OrderStatus].label.toLowerCase()}.`
          }
          style={{ padding: 48 }}
        >
          <Link to="/courses">
            <Tag color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
              Khám phá môn học →
            </Tag>
          </Link>
        </Empty>
      )}

      {!isLoading && orders && orders.length > 0 && (
        <Table<Order>
          columns={columns}
          dataSource={orders}
          rowKey="id"
          pagination={false}
          data-testid="orders-table"
        />
      )}
    </div>
  );
}

export default MyOrders;
